import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from './cx';

/** Labela + kontrola + greška (13px/600 kao u dizajnu). */
export function Field({
  label,
  error,
  hint,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-semibold">
        {label}
      </label>
      {children}
      {error ? (
        <span role="alert" className="text-xs font-medium text-danger">
          {error.includes(':') || error.includes('.')
            ? t(error.includes(':') ? error : `errors:${error}`)
            : error}
        </span>
      ) : hint ? (
        <span className="text-xs text-muted">{hint}</span>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function Input({ className, invalid, ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx('sm-input', className)}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx('sm-input', className)}
      {...rest}
    />
  );
});

/**
 * Checkbox sa labelom (jedini checkbox u panelu). Pravi `<input type="checkbox">` zbog tastature i čitača ekrana;
 * 18×18 px, boja akcenta (ili `danger` za destruktivne izbore), labela je dio klikabilne površine.
 */
export const Checkbox = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    label: ReactNode;
    tone?: 'accent' | 'danger';
    labelClassName?: string;
  }
>(function Checkbox({ label, tone = 'accent', className, labelClassName, disabled, ...rest }, ref) {
  return (
    <label
      className={cx(
        'inline-flex items-start gap-2.5 text-sm leading-normal',
        disabled ? 'cursor-not-allowed text-muted' : 'cursor-pointer',
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className={cx(
          'mt-0.5 h-[18px] w-[18px] flex-none disabled:cursor-not-allowed',
          tone === 'danger' ? 'accent-[var(--danger)]' : 'accent-[var(--accent)]',
        )}
        {...rest}
      />
      <span className={cx('min-w-0', labelClassName)}>{label}</span>
    </label>
  );
});
